import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMeeting, joinMeeting, formatTimeRemaining } from '../services/api';
import type { ChimeMeetingInfo, ChimeAttendeeInfo } from '@saywell/shared';

function WaitingRoom() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'waiting' | 'joining' | 'error'>('loading');
  const [error, setError] = useState('');
  const [agentName, setAgentName] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [meetingInfo, setMeetingInfo] = useState<{
    meeting: ChimeMeetingInfo;
    attendee: ChimeAttendeeInfo;
  } | null>(null);

  const patientName = sessionStorage.getItem('patientName') || 'Guest';

  // Check meeting status and join
  const checkAndJoin = useCallback(async () => {
    if (!code) {
      navigate('/');
      return;
    }

    try {
      // First validate the meeting exists
      const meetingResult = await getMeeting(code);

      if (!meetingResult.success || !meetingResult.meeting) {
        setError(meetingResult.error || 'Meeting not found');
        setStatus('error');
        return;
      }

      const meeting = meetingResult.meeting;
      setAgentName(meeting.agentName);
      setExpiresAt(meeting.expiresAt);

      if (meeting.status === 'expired' || meeting.status === 'ended') {
        setError('This meeting has ended');
        setStatus('error');
        return;
      }

      // Join the meeting (this puts us in the waiting room)
      setStatus('joining');
      const joinResult = await joinMeeting(code, patientName);

      if (!joinResult.success || !joinResult.meeting || !joinResult.attendee) {
        setError(joinResult.error || 'Failed to join meeting');
        setStatus('error');
        return;
      }

      // Store meeting info for video call
      setMeetingInfo({
        meeting: joinResult.meeting,
        attendee: joinResult.attendee,
      });

      // Check if agent is already in the call (status = active)
      if (meeting.status === 'active' || meeting.callStartedAt) {
        // Agent already joined, go straight to video
        sessionStorage.setItem('meetingInfo', JSON.stringify({
          meeting: joinResult.meeting,
          attendee: joinResult.attendee,
        }));
        navigate(`/call/${code}`);
      } else {
        // Wait for agent
        setStatus('waiting');
      }
    } catch (err) {
      console.error('Error joining meeting:', err);
      setError('Unable to connect. Please try again.');
      setStatus('error');
    }
  }, [code, navigate, patientName]);

  useEffect(() => {
    checkAndJoin();
  }, [checkAndJoin]);

  // Poll for meeting status updates
  useEffect(() => {
    if (status !== 'waiting' || !code) return;

    const pollInterval = setInterval(async () => {
      try {
        const result = await getMeeting(code);

        if (result.success && result.meeting) {
          // Agent joined - go to video call
          if (result.meeting.status === 'active' || result.meeting.callStartedAt) {
            if (meetingInfo) {
              sessionStorage.setItem('meetingInfo', JSON.stringify(meetingInfo));
            }
            navigate(`/call/${code}`);
          }

          // Meeting ended
          if (result.meeting.status === 'ended' || result.meeting.status === 'expired') {
            setError('This meeting has ended');
            setStatus('error');
          }
        }
      } catch (err) {
        console.error('Error polling meeting status:', err);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [status, code, navigate, meetingInfo]);

  // Update time remaining
  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const remaining = formatTimeRemaining(expiresAt);
      setTimeRemaining(remaining);

      if (remaining === 'Expired') {
        setError('This meeting has expired');
        setStatus('error');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  if (status === 'loading' || status === 'joining') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-saywell-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">
            {status === 'joining' ? 'Joining meeting...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Unable to Join</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-saywell-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        {/* Waiting animation */}
        <div className="mb-8">
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 border-4 border-saywell-primary/20 rounded-full" />
            <div className="absolute inset-0 border-4 border-saywell-primary border-t-transparent rounded-full animate-spin" />
            <div className="absolute inset-4 bg-saywell-primary/10 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-saywell-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <h1 className="text-2xl font-bold text-slate-800 mb-2">
          Waiting for {agentName || 'your provider'}
        </h1>
        <p className="text-slate-600 mb-6">
          Please wait while we connect you. Your provider will join shortly.
        </p>

        {/* Status card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
            <span>Meeting Code</span>
            <span className="font-mono font-medium text-slate-800">{code}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
            <span>Your Name</span>
            <span className="font-medium text-slate-800">{patientName}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>Time Remaining</span>
            <span className={`font-medium ${
              timeRemaining === 'Expired' ? 'text-red-600' : 'text-saywell-primary'
            }`}>
              {timeRemaining}
            </span>
          </div>
        </div>

        {/* Tips */}
        <div className="text-left bg-blue-50 rounded-lg p-4 text-sm">
          <p className="font-medium text-blue-800 mb-2">While you wait:</p>
          <ul className="space-y-1 text-blue-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">•</span>
              Make sure your camera and microphone are ready
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">•</span>
              Find a quiet, well-lit space
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">•</span>
              Have any questions or documents ready
            </li>
          </ul>
        </div>

        {/* Cancel button */}
        <button
          onClick={() => navigate('/')}
          className="mt-6 text-slate-500 hover:text-slate-700 text-sm underline"
        >
          Cancel and leave
        </button>
      </div>
    </div>
  );
}

export default WaitingRoom;
